import { useState } from "react";
import type { Movie } from "@/lib/types";
import { useToast } from "@/hooks/useToast";

export default function EpisodePanel({ movie }: { movie: Movie }) {
  const [openNo, setOpenNo] = useState<number | null>(movie.episodes[0]?.no ?? null);
  const [playing, setPlaying] = useState<{ no: number; title: string } | null>(null);
  const { push } = useToast();

  const handlePlay = (episode: { no: number; title: string }) => {
    setPlaying(episode);
    push("播放功能待接入，这里仅展示单集信息", "accent");
  };

  if (movie.episodes.length === 0) {
    return (
      <section className="mt-8" aria-label="播放说明">
        <h2 className="text-lg font-bold text-foreground-950 md:text-xl">在线观看</h2>
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/70 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-600">
              <i className="ri-play-circle-line text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">本片为长片，暂无分集信息</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                本站不提供在线播放与下载，播放按钮仅作交互演示，不会产生任何真实播放行为。
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handlePlay({ no: 1, title: movie.title })}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-play-line" />
            播放演示
          </button>
        </div>
        {playing && <PlayModal movie={movie} onClose={() => setPlaying(null)} />}
      </section>
    );
  }

  return (
    <section className="mt-8" aria-label="剧集信息">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 md:text-xl">剧集信息</h2>
          <p className="mt-1 text-xs text-foreground-500">
            {movie.status} · 点击一集展开剧情简介，播放按钮为演示
          </p>
        </div>
        <span className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs font-medium text-secondary-800">
          共 {movie.episodes.length} 集
        </span>
      </div>

      <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        {movie.episodes.map((episode) => {
          const open = openNo === episode.no;
          return (
            <div key={episode.no}>
              <button
                type="button"
                onClick={() => setOpenNo(open ? null : episode.no)}
                aria-expanded={open}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-background-100/70"
              >
                <span
                  className={`font-label flex h-7 w-7 shrink-0 items-center justify-center rounded text-xs font-bold ${
                    open ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                  }`}
                >
                  {String(episode.no).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`line-clamp-1 text-sm font-medium ${open ? "text-primary-700" : "text-foreground-900"}`}>
                    {episode.title}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">{episode.duration}</p>
                </div>
                <i
                  className={`shrink-0 text-base text-foreground-300 transition-transform ${
                    open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                  }`}
                />
              </button>
              {open && (
                <div className="anim-soft-in border-t border-background-200/70 bg-background-100/50 px-4 py-3 pl-14">
                  <p className="text-xs leading-relaxed text-foreground-600">{episode.desc}</p>
                  <button
                    type="button"
                    onClick={() => handlePlay({ no: episode.no, title: episode.title })}
                    className="mt-2.5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-play-line" />
                    播放第 {episode.no} 集
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {playing && <PlayModal movie={movie} episode={playing} onClose={() => setPlaying(null)} />}
    </section>
  );
}

function PlayModal({
  movie,
  episode,
  onClose,
}: {
  movie: Movie;
  episode?: { no: number; title: string };
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="关闭"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/60 backdrop-blur-sm"
      />
      <div className="anim-float-up relative z-10 w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
              <i className="ri-play-circle-line text-xl" />
            </span>
            <div>
              <h3 className="text-base font-bold text-foreground-950">播放功能待接入</h3>
              <p className="mt-0.5 text-xs text-foreground-500">演示提示</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭弹窗"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="mt-4 rounded-md border border-background-200 bg-background-100 p-3.5">
          <p className="text-sm font-medium text-foreground-900">《{movie.title}》</p>
          <p className="mt-1 text-xs text-foreground-600">
            {episode ? `第 ${episode.no} 集 · ${episode.title}` : "长片演示"}
            {" · "}
            {episode ? "单集时长见剧集列表" : movie.duration}
          </p>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-foreground-600">
          本站为影视内容展示站，不提供在线播放与下载能力。此按钮仅演示交互流程，
          不会加载任何视频内容，也不会显示虚假的播放进度。后续接入播放服务后即可正常使用。
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            我知道了
          </button>
        </div>
      </div>
    </div>
  );
}